import { API_BASE_URL, ApiError } from './client';

/**
 * Reporting a message, and what became of the report — §5, TROOP-12.
 *
 * **Two doors, one shape.** A member reaches these under `/units/{unitId}/` and a parent — who
 * holds no membership and would be 404'd there — under `/family/`. The payloads are identical, so
 * screens take a {@link ModerationTransport} rather than growing a branch, exactly as
 * `ConversationTransport` does for messages.
 */

export type FlagReason = 'YptConcern' | 'Inappropriate' | 'OffTopic' | 'Other';
export type FlagStatus = 'Open' | 'Reviewed' | 'Actioned' | 'Escalated';

/**
 * One report, as the person reading it is allowed to see it.
 *
 * `messageBody` is null when the message has been taken down and this reader is not one of the few
 * §5 lets read a removed body. The report survives the removal, which is rather the point of it.
 */
export interface FlagRow {
  id: string;
  messageId: string;
  conversationId: string;
  unitId: string;
  conversationTitle: string;
  raisedByPersonId: string;
  raisedByDisplayName: string;
  reason: FlagReason;
  note: string | null;
  raisedAt: string;
  status: FlagStatus;
  resolutionNote: string | null;
  resolvedByPersonId: string | null;
  resolvedByDisplayName: string | null;
  resolvedAt: string | null;
  messageAuthorPersonId: string;
  messageAuthorDisplayName: string;
  messagePostedAt: string;
  messageIsRemoved: boolean;
  messageBody: string | null;
  /** Whether this reader raised it. What turns one list into "your reports" and "the queue". */
  raisedByMe: boolean;
  /**
   * The server's answer, per row and never per response. The resolve control is gated on it, so a
   * hardcoded value would not fail closed — it would fail *silent*, taking the control away from
   * the person who does have the right and leaving nothing to diagnose (rule 14, TROOP-73).
   */
  canResolve: boolean;
}

/** What the reader picked in the reason list. Ordered as §5 orders them: consequences first. */
export const FLAG_REASONS: { value: FlagReason; label: string; hint: string }[] = [
  {
    value: 'YptConcern',
    label: 'A young person’s safety',
    hint: 'Goes to the unit’s admins straight away, and cannot be closed without an answer.',
  },
  { value: 'Inappropriate', label: 'Inappropriate', hint: 'Language, images, or how somebody was spoken to.' },
  { value: 'OffTopic', label: 'Off topic', hint: 'Wrong thread, or not something this group needs.' },
  { value: 'Other', label: 'Something else', hint: 'Tell them what you saw in your own words.' },
];

export const FLAG_STATUSES: { value: Exclude<FlagStatus, 'Open'>; label: string; hint: string }[] = [
  { value: 'Reviewed', label: 'Looked at it', hint: 'Nothing further was needed.' },
  { value: 'Actioned', label: 'Did something', hint: 'The message came down, or somebody was spoken to.' },
  { value: 'Escalated', label: 'Passed it on', hint: 'Somebody outside the unit is handling it.' },
];

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  });

  if (!response.ok) {
    let problem: { title?: string; detail?: string } = {};
    try {
      problem = await response.json();
    } catch {
      // Non-JSON body; the status is enough.
    }

    // The detail is the server's own words and is written for the person reading it -- "this
    // report is about something you wrote, so somebody else has to answer it" tells a reviewer
    // what to do next, where "403" tells them to file a support ticket.
    throw new ApiError(
      response.status,
      problem.detail ?? problem.title ?? `Failed (${response.status}).`,
    );
  }

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

export interface ModerationTransport {
  flags: () => Promise<FlagRow[]>;
  raise: (messageId: string, reason: FlagReason, note: string | null) => Promise<{ flagId: string }>;
  resolve: (
    flagId: string,
    status: Exclude<FlagStatus, 'Open'>,
    note: string | null,
  ) => Promise<unknown>;
  setRemoved: (messageId: string, removed: boolean) => Promise<unknown>;
  /** §5: unit admins, and the author their own. A 403 here is the honest answer, not a bug. */
  removedBody: (messageId: string) => Promise<{ body: string | null }>;
}

export const unitModeration = (unitId: string): ModerationTransport => ({
  flags: async () => (await call<{ rows: FlagRow[] }>(`/units/${unitId}/flags`)).rows,

  raise: (messageId, reason, note) =>
    call<{ flagId: string }>(`/units/${unitId}/messages/${messageId}/flags`, {
      method: 'POST',
      body: JSON.stringify({ reason, note }),
    }),

  resolve: (flagId, status, note) =>
    call(`/units/${unitId}/flags/${flagId}`, {
      method: 'PUT',
      body: JSON.stringify({ status, note }),
    }),

  setRemoved: (messageId, removed) =>
    call(`/units/${unitId}/messages/${messageId}/removal`, {
      method: 'PUT',
      body: JSON.stringify({ removed }),
    }),

  removedBody: (messageId) =>
    call<{ body: string | null }>(`/units/${unitId}/messages/${messageId}/removed-body`),
});

/**
 * The parent's door. No unit segment, because a parent's children span units and the guardian
 * relationship is the containment — the same shape every other `/family` route takes.
 */
export const familyModeration: ModerationTransport = {
  flags: async () => (await call<{ rows: FlagRow[] }>('/family/flags')).rows,

  raise: (messageId, reason, note) =>
    call<{ flagId: string }>(`/family/messages/${messageId}/flags`, {
      method: 'POST',
      body: JSON.stringify({ reason, note }),
    }),

  resolve: (flagId, status, note) =>
    call(`/family/flags/${flagId}`, {
      method: 'PUT',
      body: JSON.stringify({ status, note }),
    }),

  setRemoved: (messageId, removed) =>
    call(`/family/messages/${messageId}/removal`, {
      method: 'PUT',
      body: JSON.stringify({ removed }),
    }),

  removedBody: (messageId) =>
    call<{ body: string | null }>(`/family/messages/${messageId}/removed-body`),
};
